import React, { useState, useEffect } from "react";
import { useStoreDispatch } from "../../../../store/hooks";
import { setBasicDialogOpenFor, setOpenToast, setErrorMsg } from "../../../../store/helpers/helperReducers";
import { setThresholdCrossingSettingProfileModelOpenMode } from "../../../../store/api/admin/reducers";
import { IThresholdCrossingSettingProfile, IThresholdCrossingSettingAction, IThresholdCrossingSettingSchedule, IThresholdCrossingSettingRule } from "../../../../store/api/admin/types";
import { useThresholdCrossingSettingProfileData, useThresholdCrossingSettingActionData, useThresholdCrossingSettingScheduleData, useThresholdCrossingSettingRuleData } from "./hooks";
import { isError } from "../../../../utils/validations";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { TextArea } from "../../../ui/textarea";
import { Label } from "../../../ui/label";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { RadixSelectControl } from "../../../ui/radix-select";
import AutocompleteSelect from "../../../Shared/Dropdown/AutocompleteSelect";
import { getNewOptions } from "../../../../components/Tasks/helpers";
import { Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { Switch } from "../../../ui/switch";
import dayjs, { Dayjs } from "dayjs";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { useCreateThresholdCrossingSettingProfileMutation, useGetThresholdCrossingSettingProfileQuery } from "../../../../store/api/admin/adminApiSlice";
import { thresholdCrossingSettingProfilePayload, thresholdCrossingSettingActionPayload, thresholdCrossingSettingSchedulePayload, thresholdCrossingSettingRulePayload } from "../../../helpers";
import { ShadcnHeading } from "../../../ui/shadcn-heading";


  function convertTo24Hour(timeValue: string | Dayjs | null | undefined) {
    const normalized = typeof timeValue === "string"
      ? timeValue.trim()
      : dayjs(timeValue).isValid()
        ? dayjs(timeValue).format("HH:mm")
        : "";
    if (!normalized) {
      return normalized;
    }

    const as24Hour = dayjs(normalized, "HH:mm", true);
    if (as24Hour.isValid()) {
      return as24Hour.format("HH:mm");
    }

    const as12Hour = dayjs(normalized, "hh:mm A", true);
    if (as12Hour.isValid()) {
      return as12Hour.format("HH:mm");
    }

    return normalized;
  }
interface IAddEditThresholdCrossingSettingProps {
  selectedRow: IThresholdCrossingSettingProfile;
  modelOpenMode?: string;
  onProfileCreated?: (profileId: number) => void;
}

const AddEditProfile: React.FC<IAddEditThresholdCrossingSettingProps> = ({ modelOpenMode = "Add", selectedRow, onProfileCreated }) => {
  const dispatch = useStoreDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  const defaultActionRow: IThresholdCrossingSettingAction = { id: 0, performanceProfileId: 0, actionType: "", details: [] };
  const defaultScheduleRow: IThresholdCrossingSettingSchedule = { id: 0, performanceProfileId: 0, days: [], fromTime: "12:00 AM", toTime: "11:59 PM" };
  const defaultRuleRow: IThresholdCrossingSettingRule = { id: 0, performanceProfileId: 0, kpiType: "", kpi: "", operator: "", thresholdAbove: null, thresholdBelow: null, consecutiveSamples: 1 };

  const profileHook = useThresholdCrossingSettingProfileData({ ...selectedRow, enabled: false }, onProfileCreated);
  const actionHook = useThresholdCrossingSettingActionData(defaultActionRow);
  const scheduleHook = useThresholdCrossingSettingScheduleData(defaultScheduleRow);
  const ruleHook = useThresholdCrossingSettingRuleData(defaultRuleRow);
  const { refetch: refetchProfileList } = useGetThresholdCrossingSettingProfileQuery();
  const [createThresholdCrossingSettingProfile] = useCreateThresholdCrossingSettingProfileMutation();

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleClose = () => {
    setActiveStep(0);
    setIsLoadingStatus(false);

    // Reset all step data back to initial/default values.
    profileHook.setThresholdCrossingSettingProfileData({ ...selectedRow, enabled: false });
    actionHook.setThresholdCrossingSettingActionData(defaultActionRow);
    scheduleHook.setThresholdCrossingSettingScheduleData(defaultScheduleRow);
    ruleHook.setThresholdCrossingSettingRuleData(defaultRuleRow);

    // Clear local step error states.
    profileHook.setErrMsg({});
    actionHook.setErrMsg({});
    scheduleHook.setErrMsg({});
    ruleHook.setErrMsg({});

    // Clear helper/global input errors used by shared field components.
    dispatch(setErrorMsg({}));

    // Reset action details selectors.
    actionHook.setEmailGroupName([]);
    actionHook.setSnmpGroupName([]);

    dispatch(setThresholdCrossingSettingProfileModelOpenMode("Add"));
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const submitAllHandler = async () => {
    setIsLoadingStatus(true);
    try {
      const actionPayloadData = thresholdCrossingSettingActionPayload(actionHook.thresholdCrossingSettingActionData);

      const schedulePayloadData = thresholdCrossingSettingSchedulePayload({
        ...scheduleHook.thresholdCrossingSettingScheduleData,
        fromTime: convertTo24Hour(scheduleHook.thresholdCrossingSettingScheduleData.fromTime),
        toTime: convertTo24Hour(scheduleHook.thresholdCrossingSettingScheduleData.toTime)
      });

      let updatedRule = { ...ruleHook.thresholdCrossingSettingRuleData };
      if (updatedRule.kpiType === "LTE") {
        const kpiItem = ruleHook.LteKpiList?.find((k: any) => k.kpi === updatedRule.kpi);
        if (kpiItem?.thresholdAbove) {
          updatedRule.thresholdBelow = null;
        }
        else {
          updatedRule.thresholdAbove = null;
        }
      }
      if (updatedRule.kpiType === "NR") {
        const kpiItem = ruleHook.NrKpiList?.find((k: any) => k.kpi === updatedRule.kpi);
        if (kpiItem?.thresholdAbove) {
          updatedRule.thresholdBelow = null;
        }
        else {
          updatedRule.thresholdAbove = null;
        }
      }
      const rulePayloadData = thresholdCrossingSettingRulePayload(updatedRule);

      const baseProfilePayload = thresholdCrossingSettingProfilePayload({ ...profileHook.thresholdCrossingSettingProfileData });
      const profilePayloadData = {
        ...baseProfilePayload,
        id: baseProfilePayload.id === 0 ? null : baseProfilePayload.id,
        actions: [{ ...actionPayloadData, id: actionPayloadData.id === 0 ? null : actionPayloadData.id }],
        schedules: [{ ...schedulePayloadData, id: schedulePayloadData.id === 0 ? null : schedulePayloadData.id }],
        rules: [{ ...rulePayloadData, id: rulePayloadData.id === 0 ? null : rulePayloadData.id }]
      };

      const pResult: any = await createThresholdCrossingSettingProfile(profilePayloadData);
      if (pResult?.error) {
        throw pResult.error;
      }
      const profileId = pResult.data.id;
      if (onProfileCreated) {
        onProfileCreated(profileId);
      }
      refetchProfileList();
      dispatch(setOpenToast({ openToast: true, toastType: "success", message: `The Profile "${profileHook.thresholdCrossingSettingProfileData.name}" has been added successfully.` }));
      handleClose();
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: error?.message || error?.data?.message || "Failed to save profile." }));
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const isProfileValid = !!profileHook.thresholdCrossingSettingProfileData.name && !isError(profileHook.errMsg) && !profileHook.errorsPresent;
  const isActionValid = !!actionHook.thresholdCrossingSettingActionData.actionType && !!actionHook.thresholdCrossingSettingActionData.details?.length && !isError(actionHook.errMsg) && !actionHook.errorsPresent;
  const isScheduleValid = !!scheduleHook.thresholdCrossingSettingScheduleData.days?.length && !!scheduleHook.thresholdCrossingSettingScheduleData.fromTime && !!scheduleHook.thresholdCrossingSettingScheduleData.toTime && !isError(scheduleHook.errMsg) && !scheduleHook.errorsPresent;

  const hasThresholdValue = (ruleHook.thresholdCrossingSettingRuleData.thresholdAbove !== null && ruleHook.thresholdCrossingSettingRuleData.thresholdAbove !== undefined && String(ruleHook.thresholdCrossingSettingRuleData.thresholdAbove).trim() !== "") ||
    (ruleHook.thresholdCrossingSettingRuleData.thresholdBelow !== null && ruleHook.thresholdCrossingSettingRuleData.thresholdBelow !== undefined && String(ruleHook.thresholdCrossingSettingRuleData.thresholdBelow).trim() !== "");
  const isRuleValid = !!ruleHook.thresholdCrossingSettingRuleData.kpiType && !!ruleHook.thresholdCrossingSettingRuleData.kpi && hasThresholdValue && !!ruleHook.thresholdCrossingSettingRuleData.consecutiveSamples && !isError(ruleHook.errMsg) && !ruleHook.errorsPresent;

  const enableNext = () => {
    if (activeStep === 0) return isProfileValid;
    if (activeStep === 1) return isActionValid;
    if (activeStep === 2) return isScheduleValid;
    return isRuleValid;
  };

  const footer = (
    <div className="flex items-center justify-between w-full">
      <div>
        {activeStep > 0 && (
          <Button size="sm" className="text-foreground" onClick={handleBack} disabled={isLoadingStatus}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        {[0, 1, 2, 3].map((_, i) => (
          <span key={i} className={`inline-block h-2 w-2 rounded-full transition-colors ${i === activeStep ? "bg-primary" : "bg-border"}`} />
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" className="text-foreground" onClick={handleClose} disabled={isLoadingStatus}>
          Cancel
        </Button>
        {activeStep === 3 ? (
          <Button size="sm" className="text-foreground" disabled={!enableNext() || isLoadingStatus} onClick={submitAllHandler}>
            {isLoadingStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        ) : (
          <Button size="sm" className="text-foreground" disabled={!enableNext()} onClick={handleNext}>
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Sheet open={true} onClose={handleClose} title="Add Threshold Crossing Profile" footer={footer} width="520px">
      <div className="w-full py-2">
        {activeStep === 0 && <StepProfile hook={profileHook} />}
        {activeStep === 1 && <StepAction hook={actionHook} />}
        {activeStep === 2 && <StepSchedule hook={scheduleHook} />}
        {activeStep === 3 && <StepRule hook={ruleHook} />}
      </div>
    </Sheet>
  );
};

export default AddEditProfile;

const StepProfile = ({ hook }: any) => {
  const { thresholdCrossingSettingProfileData, setThresholdCrossingSettingProfileData, onBlurHandler, errMsg, setErrMsg, isLoading, name, description } = hook;

  const handleChange = (
    eventOrName: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string,
    valueArg?: string
  ) => {
    if (typeof eventOrName === "string") {
      setThresholdCrossingSettingProfileData((prev: any) => ({ ...prev, [eventOrName]: valueArg ?? "" }));
      setErrMsg((prev: any) => ({ ...prev, [eventOrName]: "" }));
      return;
    }

    const { name, value } = eventOrName.target;
    setThresholdCrossingSettingProfileData((prev: any) => ({ ...prev, [name]: value }));
    setErrMsg((prev: any) => ({ ...prev, [name]: "" }));
  };

  return (
    <form autoComplete="off">
      <div className="space-y-5 pt-2">
        <h2 style={{ margin: 0, padding: 0, lineHeight: 1, marginBottom: "10px" }}>
          <ShadcnHeading className="mb-0 text-base font-black text-foreground" as="span">
            Profile
          </ShadcnHeading>
        </h2>

        <div className="space-y-2">
          <InputField
            label="Name"
            name={name}
            disabled={isLoading}
            value={thresholdCrossingSettingProfileData?.name || ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            maxLength={255}
            tooltipText="Maximum length: 255 characters"
            autoFocus
            placeholder="e.g. DL Throughput SLA"
            required
          />
        
        </div>
        <div className="space-y-3">
          <Label htmlFor="profile-description">Description</Label>
          <TextArea
            id="profile-description"
            name={description}
            disabled={isLoading}
            value={thresholdCrossingSettingProfileData?.description || ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            maxLength={255}
            placeholder="Describe the purpose of this profile..."
            className="min-h-[96px]"
          />
        
        </div>
      </div>
    </form>
  );
};

const StepAction = ({ hook }: any) => {
  const [showActionDetailsMode, setShowActionDetailsMode] = useState(hook.thresholdCrossingSettingActionData?.actionType || "");
  const actionList = [{ text: "EMAIL", value: "EMAIL" }];

  useEffect(() => {
    if (hook.thresholdCrossingSettingActionData?.actionType) {
      setShowActionDetailsMode(hook.thresholdCrossingSettingActionData.actionType);
    }
  }, [hook.thresholdCrossingSettingActionData.actionType]);

  useEffect(() => {
    if (hook.thresholdCrossingSettingActionData.details?.length >= 0) {
      if (hook.thresholdCrossingSettingActionData.actionType === "EMAIL") {
        hook.setEmailGroupName(hook.thresholdCrossingSettingActionData.details.map((g: string) => ({ title: g, value: g })));
      } else if (hook.thresholdCrossingSettingActionData.actionType === "SNMP") {
        hook.setSnmpGroupName(hook.thresholdCrossingSettingActionData.details.map((g: string) => ({ title: g, value: g })));
      }
    }
  }, [hook.thresholdCrossingSettingActionData.details]);

  const handleChangeAction = (name: string, value: string) => {
    if (value === "EMAIL" || value === "SNMP") setShowActionDetailsMode(value);
    hook.setThresholdCrossingSettingActionData({ ...hook.thresholdCrossingSettingActionData, [name]: value, details: [] });
    hook.setEmailGroupName([]); hook.setSnmpGroupName([]);
    hook.setErrMsg({ ...hook.errMsg, [name]: "" });
  };

  const noOption = <Typography>No email group available. <Link className="nooption-link" to="/administration/email">"Click here"</Link> to add an email group.</Typography>;
  const noOptionSnmp = <Typography>No snmp group available. <Link className="nooption-link" to="/administration/northbound-interface">"Click here"</Link> to add an snmp group.</Typography>;

  return (
    <form autoComplete="off">
      <div className="space-y-5 pt-2">
        <h2 style={{ margin: 0, padding: 0, lineHeight: 1, marginBottom: "10px" }}>
          <ShadcnHeading className="mb-0 text-base font-black text-foreground" as="span">
            Action
          </ShadcnHeading>
        </h2>

        <div className="space-y-2">
          <Label htmlFor="action-type">Select Action</Label>
          <RadixSelectControl
            options={[{ label: "Select action", value: "placeholder" }, ...actionList.map(item => ({ label: item.text, value: item.value }))]}
            value={hook.thresholdCrossingSettingActionData?.actionType || "placeholder"}
            onValueChange={(val) => handleChangeAction("actionType", val === "placeholder" ? "" : val)}
          />
        </div>
        {showActionDetailsMode === "EMAIL" && (
          <div className="space-y-2">
            <Label>Email Group Name</Label>
            <AutocompleteSelect
              name="details" value={hook.emailGroupName} multiple={true} id="emailGroupName"
              options={getNewOptions(hook.emailGroupNameOptions, hook.emailGroupName)}
              label={""} margin={"normal"} size={"small"} noOptionsText={noOption}
              onChange={(e, v) => { hook.setEmailGroupName(v || []); hook.setThresholdCrossingSettingActionData({ ...hook.thresholdCrossingSettingActionData, details: v?.map((i: any) => i.value) || [] }); }}
            />
          </div>
        )}
        {showActionDetailsMode === "SNMP" && (
          <div className="space-y-2">
            <Label>Snmp Group Name</Label>
            <AutocompleteSelect
              name="details"
              value={hook.snmpGroupName}
              multiple={true} id="snmpGroupName"
              options={getNewOptions(hook.snmpGroupNameOptions, hook.snmpGroupName)}
              label={""}
              margin={"normal"}
              size={"small"}
              noOptionsText={noOptionSnmp}
              onChange={(e, v) => { hook.setSnmpGroupName(v || []); hook.setThresholdCrossingSettingActionData({ ...hook.thresholdCrossingSettingActionData, details: v?.map((i: any) => i.value) || [] }); }}
            />
          </div>
        )}
      </div>
    </form>
  );
};

const StepSchedule = ({ hook }: any) => {
  const parseScheduleTime = (timeValue: string | Dayjs | null | undefined) => {
    if (!timeValue) {
      return dayjs("");
    }
    if (dayjs.isDayjs(timeValue)) {
      return timeValue;
    }

    const normalized = String(timeValue).trim();

    const as24Hour = dayjs(normalized, "HH:mm", true);
    if (as24Hour.isValid()) return as24Hour;

    const as24HourWithSeconds = dayjs(normalized, "HH:mm:ss", true);
    if (as24HourWithSeconds.isValid()) return as24HourWithSeconds;

    const as12Hour = dayjs(normalized, "hh:mm A", true);
    if (as12Hour.isValid()) return as12Hour;

    const asIsoDateTime = dayjs(normalized);
    if (asIsoDateTime.isValid()) return asIsoDateTime;

    const timeMatch = normalized.match(/(\d{2}:\d{2})(?::\d{2})?/);
    if (timeMatch?.[1]) {
      const fromExtracted = dayjs(timeMatch[1], "HH:mm", true);
      if (fromExtracted.isValid()) return fromExtracted;
    }

    return dayjs("");
  };

  const daysOfWeek = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const dayDisplayMap: { [key: string]: string } = { "MON": "M", "TUE": "T", "WED": "W", "THU": "T", "FRI": "F", "SAT": "S", "SUN": "S" };
  const [selectedDays, setSelectedDays] = useState<string[]>(hook.thresholdCrossingSettingScheduleData?.days || []);
  const [isAllDay, setIsAllDay] = useState<boolean>(
    parseScheduleTime(hook.thresholdCrossingSettingScheduleData?.fromTime).format("HH:mm") === "00:00" &&
    parseScheduleTime(hook.thresholdCrossingSettingScheduleData?.toTime).format("HH:mm") === "23:59"
  );

  const activeDays = selectedDays.length > 0 ? selectedDays : hook.thresholdCrossingSettingScheduleData?.days || [];

  const handleAllDayChange = (checked: boolean) => {
    setIsAllDay(checked);
    if (checked) {
      hook.setThresholdCrossingSettingScheduleData({ ...hook.thresholdCrossingSettingScheduleData, fromTime: "00:00", toTime: "23:59" });
      hook.setErrMsg({ ...hook.errMsg, fromTime: "", toTime: "" });
    } else {
      const currentTime = dayjs();
      hook.setThresholdCrossingSettingScheduleData({ ...hook.thresholdCrossingSettingScheduleData, fromTime: currentTime.format("HH:mm"), toTime: currentTime.add(15, "minute").format("HH:mm") });
    }
  };

  const handleDayChange = (day: string) => {
    const currentDays = activeDays;
    const newDays = currentDays.includes(day) ? currentDays.filter((d: string) => d !== day) : [...currentDays, day];
    setSelectedDays(newDays);
    hook.setThresholdCrossingSettingScheduleData({ ...hook.thresholdCrossingSettingScheduleData, days: newDays.sort((a: string, b: string) => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b)) });
    hook.setErrMsg({ ...hook.errMsg, days: "" });
  };

  const handleFromChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const newFromTime = newValue.format("HH:mm");
      const toTime = parseScheduleTime(hook.thresholdCrossingSettingScheduleData.toTime);
      if (toTime.isValid() && newValue.isAfter(toTime)) {
        hook.setThresholdCrossingSettingScheduleData({ ...hook.thresholdCrossingSettingScheduleData, fromTime: newFromTime, toTime: newValue.add(15, "minute").format("HH:mm") });
      } else {
        hook.setThresholdCrossingSettingScheduleData({ ...hook.thresholdCrossingSettingScheduleData, fromTime: newFromTime });
      }
      hook.setErrMsg({ ...hook.errMsg, fromTime: "" });
    }
  };

  const handleToChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const fromTime = parseScheduleTime(hook.thresholdCrossingSettingScheduleData.fromTime);
      hook.setThresholdCrossingSettingScheduleData({ ...hook.thresholdCrossingSettingScheduleData, toTime: newValue.format("HH:mm") });
      if (!fromTime.isValid() || newValue.isAfter(fromTime)) {
        hook.setErrMsg({ ...hook.errMsg, toTime: "" });
      } else {
        hook.setErrMsg({ ...hook.errMsg, toTime: "End time must be greater than start time" });
      }
    }
  };

  return (
    <form autoComplete="off">
      <div className="space-y-5 pt-2">
        <h2 style={{ margin: 0, padding: 0, lineHeight: 1, marginBottom: "10px" }}>
          <ShadcnHeading className="mb-0 text-base font-black text-foreground" as="span">
            Schedule
          </ShadcnHeading>
        </h2>
        <p className="text-xs italic" style={{ color: "var(--warning)" }}>Set the days and time this profile will run.</p>
        <div className="space-y-2">
          <Label>Days</Label>
          <div className="flex gap-2 flex-wrap">
            {daysOfWeek.map((day) => (
              <button
                key={day} type="button" name="days" onClick={() => handleDayChange(day)}
                className={`h-9 w-9 rounded-full text-sm font-medium border transition-colors ${activeDays.includes(day) ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border hover:opacity-80"}`}
              >
                {dayDisplayMap[day]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between space-x-2">
          <Label htmlFor="all-day-switch" className="cursor-pointer">All Day</Label>
          <Switch id="all-day-switch" checked={isAllDay} onCheckedChange={handleAllDayChange} />
        </div>
        {!isAllDay && (
          <div className="space-y-2 mt-0">
            <div className="flex flex-row gap-4">
              <div className="flex flex-col gap-2 flex-1">
                <Label>Start Time</Label>
               
                <Input type="time"
                  name="fromTime"
                  value={parseScheduleTime(hook.thresholdCrossingSettingScheduleData.fromTime).format("HH:mm")}
                  onChange={(e) => handleFromChange(parseScheduleTime(e.target.value))}
                  className="h-9" />
                  {hook.errMsg?.fromTime && (
                    <p className="text-xs font-sans text-destructive">
                      {hook.errMsg?.fromTime}
                    </p>
                  )}

              </div>
               <span className="text-muted-foreground mt-6">–</span>
              <div className="flex flex-col gap-2 flex-1">
                <Label>End Time</Label>
               
                <Input type="time"
                  name="toTime"
                  value={parseScheduleTime(hook.thresholdCrossingSettingScheduleData.toTime).format("HH:mm")}
                  onChange={(e) => handleToChange(parseScheduleTime(e.target.value))}
                  className="h-9" />
                    {hook.errMsg?.toTime && (
                    <p className="text-xs font-sans text-destructive">
                      {hook.errMsg?.toTime}
                    </p>
                  )}

              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

const StepRule = ({ hook }: any) => {
  const [kpiLists, setKpiLists] = useState<any[]>([]);
  const [kpiThresholdAbove, setKpiThresholdAbove] = useState("");
  const [kpiMinValue, setKpiMinValue] = useState(0);
  const [kpiMaxValue, setKpiMaxValue] = useState(0);

  useEffect(() => {
    if (hook.thresholdCrossingSettingRuleData.kpiType === "LTE") {
      setKpiLists(hook.LteKpiList?.map((k: any) => ({ text: k.kpi, value: k.kpi })) || []);
      const kItem = hook.LteKpiList?.find((k: any) => k.kpi === hook.thresholdCrossingSettingRuleData.kpi);
      if (kItem) {
        setKpiThresholdAbove(kItem.thresholdAbove ? "YES" : "NO");
        setKpiMinValue(kItem.minValue);
        setKpiMaxValue(kItem.maxValue);
      }
    } else if (hook.thresholdCrossingSettingRuleData.kpiType === "NR") {
      setKpiLists(hook.NrKpiList?.map((k: any) => ({ text: k.kpi, value: k.kpi })) || []);
      const kItem = hook.NrKpiList?.find((k: any) => k.kpi === hook.thresholdCrossingSettingRuleData.kpi);

      if (kItem) {
        setKpiThresholdAbove(kItem.thresholdAbove ? "YES" : "NO");
        setKpiMinValue(kItem.minValue);
        setKpiMaxValue(kItem.maxValue);
      }
    }
  }, [hook.thresholdCrossingSettingRuleData.kpiType, hook.thresholdCrossingSettingRuleData.kpi, hook.LteKpiList, hook.NrKpiList]);

  const kpiTypeList = [{ text: "LTE", value: "LTE" }, { text: "NR", value: "NR" }];

  return (
    <form autoComplete="off">
      <div className="space-y-4 pt-2">
        <h2 style={{ margin: 0, padding: 0, lineHeight: 1, marginBottom: "10px" }}>
          <ShadcnHeading className="mb-0 text-base font-black text-foreground" as="span">
            Alert when...
          </ShadcnHeading>
        </h2>
        <div className="space-y-2">
          <Label>Kpi Type <span style={{ color: "var(--destructive)" }}>*</span></Label>
          <RadixSelectControl
            options={[{ label: "Select Kpi Type", value: "placeholder" }, ...kpiTypeList.map(item => ({ label: item.text, value: item.value }))]}
            value={hook.thresholdCrossingSettingRuleData?.kpiType || "placeholder"}
            onValueChange={(val) => {
              const v = val === "placeholder" ? "" : val;
              hook.setThresholdCrossingSettingRuleData({ ...hook.thresholdCrossingSettingRuleData, kpiType: v, kpi: "", thresholdAbove: null, thresholdBelow: null });
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Kpi <span style={{ color: "var(--destructive)" }}>*</span></Label>
          <RadixSelectControl
            options={[{ label: "Select Kpi", value: "placeholder" }, ...kpiLists.map(item => ({ label: item.text, value: item.value }))]}
            value={hook.thresholdCrossingSettingRuleData?.kpi || "placeholder"}
            onValueChange={(val) => hook.setThresholdCrossingSettingRuleData({ ...hook.thresholdCrossingSettingRuleData, kpi: val === "placeholder" ? "" : val })}
          />
        </div>
        {kpiThresholdAbove === "YES" && (
          <div className="space-y-2">
            <InputField
              label="Alert When Above"
              required
              name="thresholdAbove"
              value={hook.thresholdCrossingSettingRuleData?.thresholdAbove || ""}
              type="number"
              min={kpiMinValue}
              max={kpiMaxValue}
              onBlur={hook.onBlurHandler}
              onChange={(name, value) => hook.setThresholdCrossingSettingRuleData({ ...hook.thresholdCrossingSettingRuleData, thresholdAbove: value })}
            />
          </div>
        )}
        {kpiThresholdAbove === "NO" && (
          <div className="space-y-2">
            <InputField
              label="Alert When Below"
              required
              name="thresholdBelow"
              value={hook.thresholdCrossingSettingRuleData?.thresholdBelow || ""}
              type="number"
              min={kpiMinValue}
              max={kpiMaxValue}
              onBlur={hook.onBlurHandler}
              onChange={(name, value) => hook.setThresholdCrossingSettingRuleData({ ...hook.thresholdCrossingSettingRuleData, thresholdBelow: value })}
            />
          </div>
        )}
        <div className="space-y-2">
          <InputField
            label="Consecutive Samples"
            required
            name="consecutiveSamples"
            min={1}
            max={10}
            value={hook.thresholdCrossingSettingRuleData?.consecutiveSamples || ""}
            type="number"
            onBlur={hook.onBlurHandler}
            onChange={(name, value) => hook.setThresholdCrossingSettingRuleData({ ...hook.thresholdCrossingSettingRuleData, consecutiveSamples: value })}
          />
        </div>
      </div>
    </form>
  );
};
